import React, { useCallback, useState } from "react";
import { RestoreIcon } from "@sanity/icons/Restore";
import {
  DocumentActionComponent,
  DocumentActionDescription,
  SanityClient,
  useClient
} from "sanity";
import { SanityDocument } from "@sanity/client";
import {
  fixDocumentMultilingual,
  FixMultilingualResult
} from "./fixMultilingualTransform";
import FixMultilingualDialog from "./FixMultilingualDialog";

async function applyFix(
  client: SanityClient,
  draft: SanityDocument,
  transformed: SanityDocument
) {
  const { _id, _rev, _type, _createdAt, _updatedAt, ...fieldsToSet } =
    transformed;

  const transaction = client.transaction();
  transaction.patch(draft._id, {
    set: fieldsToSet,
    ifRevisionID: draft._rev
  });

  await transaction.commit();
}

const FixMultilingualAction: DocumentActionComponent = ({
  draft,
  onComplete
}) => {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [transformResult, setTransformResult] =
    useState<FixMultilingualResult | null>(null);
  const client = useClient({ apiVersion: "2021-10-21" });

  const onHandle = useCallback(() => {
    const result = fixDocumentMultilingual(draft!);
    setTransformResult(result);
    setDialogOpen(true);
  }, [draft]);

  const onConfirm = useCallback(() => {
    if (!transformResult) return;
    applyFix(client, draft!, transformResult.transformed)
      .then(onComplete)
      .catch((e) => console.error("Fix multilingual failed", e));
    setDialogOpen(false);
  }, [client, draft, onComplete, transformResult]);

  const onClose = useCallback(() => {
    setDialogOpen(false);
    setTransformResult(null);
  }, []);

  return {
    label: "Fix document Schema",
    icon: RestoreIcon,
    disabled: !draft,
    onHandle,
    dialog: isDialogOpen &&
      transformResult !== null && {
        type: "dialog",
        header: "Fix document Schema",
        width: "medium",
        onClose,
        content: (
          <FixMultilingualDialog
            converted={transformResult.converted}
            convertedPaths={transformResult.convertedPaths}
            languageFixed={transformResult.languageFixed}
            languageFixedPaths={transformResult.languageFixedPaths}
            unsupportedLanguages={transformResult.unsupportedLanguages}
            onConfirm={onConfirm}
            onClose={onClose}
          />
        )
      }
  } satisfies DocumentActionDescription;
};

export default FixMultilingualAction;
